/**
 * User Panel Component (Discord-style)
 *
 * Fixed bottom panel in sidebar showing:
 * - User avatar with small status indicator
 * - Username and status text
 * - Always-visible mic/deafen/settings controls
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth.store';
import { useVoice } from '@/contexts/VoiceContext';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { useDND } from '@/hooks/useDND';
import { useUserSettings } from '@/hooks/useUserSettings';
import { updateStatus as updateStatusApi } from '@/services/user-settings.service';
import Avatar from '@/components/common/Avatar';
import { Mic, MicOff, Headphones, HeadphoneOff, Settings } from 'lucide-react';
import type { UserSettings } from '@/services/user-settings.service';

interface UserPanelProps {
  onOpenSettings: () => void;
}

type UserStatus = 'online' | 'away' | 'dnd' | 'offline';

const statusConfig: Record<UserStatus, { label: string; color: string }> = {
  online: { label: 'Онлайн', color: 'bg-green-500' },
  away: { label: 'Отсъстващ', color: 'bg-yellow-500' },
  dnd: { label: 'Не безпокойте', color: 'bg-red-500' },
  offline: { label: 'Невидим', color: 'bg-gray-500' },
};

export const UserPanel: React.FC<UserPanelProps> = ({ onOpenSettings }) => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { voiceState: dmVoiceState, toggleMute: toggleDmMute, toggleDeafen: toggleDmDeafen } = useVoice();
  const { state: liveKitState, toggleMute: toggleLiveKitMute, toggleDeafen: toggleLiveKitDeafen } = useLiveKit();
  const { status: dndStatus } = useDND();
  const { data: settings } = useUserSettings();

  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<UserStatus>('online');
  const [statusInitialized, setStatusInitialized] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Voice state
  const isInDmCall = !!dmVoiceState.activeSession;
  const isInChannelVoice = liveKitState.isConnected;
  const isInAnyVoice = isInDmCall || isInChannelVoice;
  const isMuted = isInDmCall ? dmVoiceState.isMuted : liveKitState.isMuted;
  const isDeafened = isInDmCall ? dmVoiceState.isDeafened : liveKitState.isDeafened;

  const handleMuteToggle = () => {
    if (isInDmCall) toggleDmMute();
    else if (isInChannelVoice) toggleLiveKitMute();
  };

  const handleDeafenToggle = () => {
    if (isInDmCall) toggleDmDeafen();
    else if (isInChannelVoice) toggleLiveKitDeafen();
  };

  // Initialize status from the persisted value (feature #43) once settings load
  // (adjust state during render instead of in an effect).
  if (!statusInitialized) {
    const persisted = settings?.status;
    if (persisted && ['online', 'away', 'dnd', 'offline'].includes(persisted)) {
      setStatusInitialized(true);
      setCurrentStatus(persisted as UserStatus);
    }
  }

  // DND sync: enabling Do Not Disturb forces the status to "dnd"
  // (adjust state during render on change).
  const [prevDndStatus, setPrevDndStatus] = useState(dndStatus);
  if (dndStatus !== prevDndStatus) {
    setPrevDndStatus(dndStatus);
    if (dndStatus?.enabled) setCurrentStatus('dnd');
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusChange = async (status: UserStatus) => {
    if (status === currentStatus) {
      setShowStatusMenu(false);
      return;
    }
    const previous = currentStatus;
    // Optimistic update
    setCurrentStatus(status);
    setShowStatusMenu(false);
    setStatusInitialized(true);

    try {
      await updateStatusApi(status);
      // Keep the cached settings in sync so a later refetch doesn't revert it.
      queryClient.setQueryData<UserSettings>(['userSettings'], (old) =>
        old ? { ...old, status } : old
      );
    } catch {
      // Revert on failure
      setCurrentStatus(previous);
      toast.error('Грешка при промяна на статуса');
    }
  };

  const status = statusConfig[currentStatus];

  return (
    <div className="bg-dark-bg-primary/80 border-t border-dark-border-primary/30 px-2 py-[10px]">
      <div className="flex items-center gap-2">
        {/* Avatar with Status Indicator */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={() => setShowStatusMenu(!showStatusMenu)}
            className="relative group focus:outline-none rounded-full"
            title="Промени статус"
          >
            {user && <Avatar user={user} size="sm" />}
            {/* Status dot - Discord style (small, bottom-right) */}
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-[14px] h-[14px] rounded-full border-[3px] border-dark-bg-primary ${status.color}`}
            />
          </button>

          {/* Status Dropdown */}
          {showStatusMenu && (
            <div className="absolute bottom-full left-0 mb-2 w-[200px] bg-dark-bg-primary rounded-md shadow-xl border border-dark-border-primary overflow-hidden z-50">
              <div className="px-3 py-2 border-b border-dark-border-primary">
                <p className="text-[11px] font-semibold text-dark-text-muted">Статус</p>
              </div>
              {(Object.entries(statusConfig) as [UserStatus, typeof status][]).map(([value, config]) => (
                <button
                  key={value}
                  onClick={() => handleStatusChange(value)}
                  className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-dark-bg-hover rounded-md transition-colors duration-150 ${
                    currentStatus === value ? 'bg-dark-bg-hover/50' : ''
                  }`}
                >
                  <div className={`w-[10px] h-[10px] rounded-full ${config.color}`} />
                  <span className="text-sm text-dark-text-secondary">{config.label}</span>
                  {currentStatus === value && (
                    <svg className="ml-auto w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0 mr-1">
          <p className="text-sm font-semibold text-white truncate leading-tight">
            {user?.display_name || 'User'}
          </p>
          <p className="text-[11px] text-dark-text-secondary truncate leading-tight">
            {status.label}
          </p>
        </div>

        {/* Action Buttons - Always visible (Discord style) */}
        <div className="flex items-center">
          {/* Mic Button */}
          <button
            onClick={handleMuteToggle}
            disabled={!isInAnyVoice}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-150 active:scale-95 ${
              !isInAnyVoice
                ? 'text-dark-text-muted cursor-not-allowed'
                : isMuted
                ? 'text-red-400 bg-red-500/10 hover:bg-red-500/20'
                : 'text-dark-text-secondary hover:bg-dark-bg-hover'
            }`}
            title={!isInAnyVoice ? 'Не сте в гласов канал' : isMuted ? 'Включи микрофон' : 'Изключи микрофон'}
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>

          {/* Deafen Button */}
          <button
            onClick={handleDeafenToggle}
            disabled={!isInAnyVoice}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-150 active:scale-95 ${
              !isInAnyVoice
                ? 'text-dark-text-muted cursor-not-allowed'
                : isDeafened
                ? 'text-red-400 bg-red-500/10 hover:bg-red-500/20'
                : 'text-dark-text-secondary hover:bg-dark-bg-hover'
            }`}
            title={!isInAnyVoice ? 'Не сте в гласов канал' : isDeafened ? 'Включи звук' : 'Изключи звук'}
          >
            {isDeafened ? <HeadphoneOff className="w-5 h-5" /> : <Headphones className="w-5 h-5" />}
          </button>

          {/* Settings Button */}
          <button
            onClick={onOpenSettings}
            className="w-8 h-8 flex items-center justify-center rounded-md text-dark-text-secondary hover:bg-dark-bg-hover hover:text-dark-text-primary transition-colors duration-150 active:scale-95"
            title="Потребителски настройки"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;
